import { useState, type ReactNode } from 'react'

import type { LabelLayout } from '@aeris/printing'
import { Button } from '@aeris/ui'

import { PX_PER_MM, withZoom } from '../lib/label-preview.ts'
import type { LabelTemplate } from '../lib/label-template.ts'

/**
 * A prévia da folha de etiquetas, com as duas vistas (F127, extraída na F195).
 *
 * Era a conta de dentro do diálogo de UM produto; a tela da fila mostrava um
 * `iframe` de altura fixa, cortado à direita e sem como ler um adesivo. Agora
 * as duas telas usam esta: a folha inteira cabendo na janela, para ver onde os
 * adesivos caem (inclusive as posições puladas, em branco); ou um adesivo
 * ampliado, para ler o que sai escrito nele.
 *
 * A prévia é o MESMO desenho da impressão (`htmlFor`), só com escala por cima.
 *
 * O adesivo ampliado é desenhado SEM pular posições: a vista mostra o canto de
 * cima à esquerda da folha, e com "começar na 5" ali estaria um branco.
 */

const PREVIEW_WIDTH_PX = 600

export const LabelSheetPreview = ({
  title,
  htmlFor,
  layout,
  simple,
  labelsOnSheet,
  skip,
}: {
  title: string
  /** O documento com `labels` etiquetas, pulando `skip` posições. */
  htmlFor: (labels: number, skip: number) => string
  /** O modelo da loja; ausente é o formato simples do Aeris. */
  layout: LabelLayout | undefined
  simple: LabelTemplate
  /** Quantas etiquetas desenhar na primeira folha — não as 500 do lote. */
  labelsOnSheet: number
  skip: number
}): ReactNode => {
  const [enlarged, setEnlarged] = useState(false)

  const pageWidthMm =
    layout === undefined ? simple.widthMm * simple.columns : layout.page.widthMm
  const sightWidthMm = enlarged
    ? layout === undefined
      ? simple.widthMm
      : layout.page.marginLeftMm + layout.label.widthMm + 2
    : pageWidthMm
  const sightHeightMm = enlarged
    ? layout === undefined
      ? simple.heightMm
      : layout.page.marginTopMm + layout.label.heightMm + 2
    : layout === undefined
      ? simple.heightMm * Math.min(labelsOnSheet, 4)
      : layout.page.heightMm
  const zoom = Math.min(
    enlarged ? 4 : 1,
    PREVIEW_WIDTH_PX / (sightWidthMm * PX_PER_MM)
  )
  const heightPx = Math.ceil(sightHeightMm * PX_PER_MM * zoom) + 4

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          aria-pressed={enlarged}
          onClick={() => {
            setEnlarged((current) => !current)
          }}
        >
          {enlarged ? 'Ver a folha inteira' : 'Ampliar uma etiqueta'}
        </Button>
      </div>
      <iframe
        title={title}
        srcDoc={withZoom(
          enlarged ? htmlFor(1, 0) : htmlFor(labelsOnSheet, skip),
          zoom
        )}
        style={{ height: `${String(heightPx)}px` }}
        className="max-h-[28rem] w-full rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken)"
      />
    </div>
  )
}
